import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination } from '@ui/src';
import {
  RiskScoreItem,
  DeliveryChannel,
  DeliveryChannelValidators,
} from '../../../types';
import { corporateClientsRiskApi } from '../services';
import ROUTES from '../../../../routes';

export interface CorporateClientsDeliveryChannelsListState {
  values?: DeliveryChannel[];
  pagination?: Pagination;
  selectedList: string[];
  validators?: DeliveryChannelValidators;
  errorsList?: string;
}

const initialState: CorporateClientsDeliveryChannelsListState = {
  values: undefined,
  selectedList: [],
};

export const corporateClientsDeliveryChannelsSlice = createSlice({
  name: 'corporateClientsDeliveryChannels',
  initialState,
  reducers: {
    selectList: (state, action: PayloadAction<string[]>) => {
      state.selectedList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.getCorporateDeliveryChannels
        .matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.getCorporateDeliveryChannels
        .matchRejected,
      (state, action: any) => {
        state.values = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.getCorporateDeliveryChannels
        .matchFulfilled,
      (state, action: PayloadAction<Response<RiskScoreItem>>) => {
        state.values = action.payload.data.edges
          .map((item) => ({
            id: item.node.id,
            riskScore: item.node.riskScore,
            name: item.node.name,
            scoreAction: item.node.riskScoreRange.action,
            riskCategory: item.node.riskScoreRange.name,
            riskScoreRangeId: item.node.riskScoreRangeId,
            url: generatePath(
              ROUTES.CORPORATE_CLIENTS_RISK_DELIVERY_CHANNELS_EDIT,
              {
                channelId: item.node.id,
              },
            ),
          }))
          .sort((a, b) => b.riskScore - a.riskScore);

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.getCorporateDeliveryChannelValidators
        .matchFulfilled,
      (state, action: PayloadAction<{ data: DeliveryChannelValidators }>) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.updateCorporateDeliveryChannel
        .matchFulfilled,
      (state, action: PayloadAction<{ data: RiskScoreItem }>) => {
        const { data } = action.payload;

        const nextList = state.values?.map((item) => {
          let result = { ...item };

          if (result.id === data.id) {
            result = {
              id: data.id,
              riskScore: data.riskScore,
              name: data.name,
              scoreAction: data.riskScoreRange.action,
              riskCategory: data.riskScoreRange.name,
              riskScoreRangeId: data.riskScoreRangeId,
              url: generatePath(
                ROUTES.CORPORATE_CLIENTS_RISK_DELIVERY_CHANNELS_EDIT,
                {
                  channelId: data.id,
                },
              ),
            };
          }

          return result;
        });

        state.values = nextList;
      },
    );
  },
});

export const { selectList } = corporateClientsDeliveryChannelsSlice.actions;

export default corporateClientsDeliveryChannelsSlice.reducer;
